/** @format */
'use client';
import { elasticToResponseMapper } from '@/common/lib/mappers/response';
import Input from '@atoms/Input';
import { TableKey } from '@common/application/enums/TableKey';
import useTableSettings from '@common/application/hooks/useTableSettings/useTableSettings';
import useThrottledCallback from '@common/application/hooks/useThrottledCallback';
import { ElasticPageHistory, ElasticResponseBody } from '@common/types/elastic';
import { css } from '@emotion/react';
import Table from '@molecules/Table';
import TableSettingsModalTrigger from '@organisms/TableSettingsModalTrigger';
import { useParams } from 'next/navigation';
import React, { ReactElement, useMemo, useState } from 'react';
import { Column, Row } from 'react-table';
import styles from './SearchableTable.module.css';

interface SearchableTableProps<DataType extends object> {
  id?: string;
  title: ReactElement | string;
  tableKey: TableKey;
  search: string;
  setSearch: (search: string) => void;
  columns: Column<DataType>[];
  data?: ElasticResponseBody<DataType>;
  previousPage?: () => void;
  nextPage?: (data: ElasticResponseBody<DataType>) => void;
  pageHistory?: ElasticPageHistory;
  loading?: boolean;
  className?: string;
  unpaginated?: boolean;
  onClickRow?: (row: Row<DataType>) => void;
  header?: boolean;
  throttle?: boolean;
  hideTable?: boolean;
  href?: (row: Row<DataType>) => string;
  // These are intended to be the state and dispatch returned from useTableSorting custom hook
  onSort?(sort: Record<string, 'asc' | 'desc'> | null): void;

  sort?: Record<string, 'asc' | 'desc'>;

  disableSorting?: boolean;

  renderRowSubComponent?(row: Row<DataType>): ReactElement;

  allowRenderToCard?: boolean;
}

function SearchableTable<DataType extends object>(props: SearchableTableProps<DataType>) {
  const { settings, setSettings, defaultSort, saveChanges, undoChanges, isLoaded } = useTableSettings(props.tableKey);
  const [input, _setInput] = useState(props.search);
  const setThrottledSearch = useThrottledCallback((value: string) => props.setSearch(value), [props.setSearch], props.throttle ? 250 : 0);
  const params = useParams();
  const routerQuery = params?.q;

  const data = elasticToResponseMapper(props.data);

  const setInput = (value: string) => {
    _setInput(value);
    setThrottledSearch(value);
  };

  if (routerQuery) {
    setInput(routerQuery as string);
  }

  // Hide and set order of columns based on user settings
  const filteredColumns = useMemo(
    () =>
      props.columns
        .sort((a, b) => {
          return settings.columns.includes(a.id as string) && settings.columns.indexOf(b.id as string) > settings.columns.indexOf(a.id as string) ? -1 : 1;
        })
        .filter((col) => !settings?.hiddenColumns?.includes(col.id as string)),
    [props.columns, settings],
  );

  return (
    <div id={props.id} style={{ maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className={styles.header}>
        <h1>{props.title}</h1>
        <TableSettingsModalTrigger settings={settings} setSettings={setSettings} saveChanges={saveChanges} undoChanges={undoChanges} tableKey={props.tableKey} />
      </div>
      <Input.Search
        css={css`
          width: 100%;
          margin-bottom: 16px;
        `}
        value={input}
        onChange={setInput}
      />
      {props.data && (
        <div style={{ flex: 1, minHeight: 0 }}>
          <Table<DataType>
            columns={filteredColumns}
            // data={props.data}
            data={data}
            pageHistory={props.pageHistory}
            nextPage={() => props.nextPage?.(props.data!)}
            previousPage={() => props.previousPage?.()}
            sort={props.sort}
            onSort={props.onSort}
            defaultSort={settings.defaultSort}
            onClickRow={props.onClickRow}
            loading={props.loading}
            // loading={props.loading || !isLoaded}
            renderRowSubComponent={props.renderRowSubComponent}
            disableSorting={props.disableSorting}
            allowRenderToCard={true}
            href={props.href}
            // allowRenderToCard={props.allowRenderToCard}
          />
        </div>
      )}
    </div>
  );
}

export default SearchableTable;
